import React, { useState, useRef, useEffect } from "react";
import Image from "../assets/ReconPro Logo .png";
import { FiMenu, FiX, FiChevronDown } from "react-icons/fi";
import { Link as RouterLink } from "react-router-dom";
import { HashLink as ScrollLink } from "react-router-hash-link";
import { scroller } from "react-scroll";

export default function Navbar() {
  const [showMenu, setShowMenu] = useState(false);
  const [showDesktopPricingDropdown, setShowDesktopPricingDropdown] = useState(false);
  const [showMobilePricingDropdown, setShowMobilePricingDropdown] = useState(false);
  const desktopDropdownRef = useRef(null);
  const pricingButtonRef = useRef(null);

  const closeMenu = () => setShowMenu(false);

  const handleSignInClick = () => {
    const authToken = localStorage.getItem('authToken');
    if (authToken) {
      window.location.href = '/acc_profile';
    } else {
      window.location.href = '/sign-in';
    }
    closeMenu();
  };

  const handleScrollToSection = (target) => {
    if (target === "product" || target === "pricing") {
      window.scrollTo(0, 0);
    } else {
      scroller.scrollTo(target, {
        smooth: true,
        duration: 500,
        offset: -100,
      });
    }
    closeMenu();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        desktopDropdownRef.current &&
        !desktopDropdownRef.current.contains(event.target) &&
        pricingButtonRef.current &&
        !pricingButtonRef.current.contains(event.target)
      ) {
        setShowDesktopPricingDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <nav className="fixed top-0 left-0 w-full z-50 shadow-lg bg-[#f8f9fa]">
      <div className="max-w-[1300px] mx-auto flex justify-between items-center py-4 px-6">
        {/* Logo */}
        <RouterLink to="/" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          <img src={Image} alt="Logo" className="h-12 w-50 cursor-pointer" />
        </RouterLink>

        {/* Desktop Navigation */}
        <ul className="hidden md:flex space-x-9 items-center">
          {[
            { name: "Home", to: "/", section: "home" },
            { name: "About Us", to: "/#about", section: "about", isScroll: true },
            { name: "Product", to: "/gst-reconciliation", section: "product" },
            { name: "Contact Us", to: "/#contact", section: "contact", isScroll: true },
          ].map(({ name, to, section, isScroll }) => (
            <li key={name}>
              {isScroll ? (
                <ScrollLink to={to} smooth onClick={() => handleScrollToSection(section)}>
                  <button className="hover:font-bold">{name}</button>
                </ScrollLink>
              ) : (
                <RouterLink to={to} onClick={() => {
                  if (section === "home") {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                  handleScrollToSection(section);
                }}>
                  <button className="hover:font-bold">{name}</button>
                </RouterLink>
              )}
            </li>
          ))}

          {/* Desktop Pricing Dropdown */}
          <li className="relative">
            <button
              ref={pricingButtonRef}
              className="hover:font-bold flex items-center focus:outline-none"
              onClick={() => setShowDesktopPricingDropdown(!showDesktopPricingDropdown)}
            >
              Pricing <FiChevronDown className="ml-1" />
            </button>
            
            {showDesktopPricingDropdown && (
              <div 
                ref={desktopDropdownRef}
                className="absolute left-0 mt-4 w-56 bg-white rounded-md shadow-lg py-2 z-50 border border-gray-200"
              >
                {/* GST Reconciliation Dropdown Item */}
                <RouterLink 
                  to="/pricing" 
                  onClick={() => {
                    handleScrollToSection("pricing");
                    setShowDesktopPricingDropdown(false);
                  }}
                >
                  <div className="block w-full text-left px-4 py-3 text-gray-800 hover:bg-gray-900 hover:font-bold hover:bg-gray-800 hover:text-white transition-colors cursor-pointer">
                    <div className="font-medium">GST Reconciliation</div>
                    <div className="text-sm text-gray-600 hover:text-white">View pricing plans for GST reconciliation</div>
                  </div>
                </RouterLink>
                
                {/* Divider */}
                <div className="border-t my-1"></div>
                
                {/* Ledger Pro Dropdown Item */}
                <RouterLink 
                  to="/ledger-pro" 
                  onClick={() => {
                    setShowDesktopPricingDropdown(false);
                  }}
                >
                  <div className="block w-full text-left px-4 py-3 text-gray-800 hover:bg-gray-100 hover:font-bold hover:bg-gray-800 hover:text-white transition-colors cursor-pointer">
                    <div className="font-medium">Ledger Pro</div>
                    <div className="text-sm text-gray-600 hover:text-white">View pricing for Ledger Pro</div>
                  </div>
                </RouterLink>
              </div>
            )}
          </li>
        </ul>

        {/* Desktop Buttons */}
        <div className="hidden md:flex space-x-4">
          <button onClick={handleSignInClick} className="border-2 border-gray-400 text-white px-4 py-2 w-32 hover:font-bold rounded-md bg-gray-800">
            Sign In
          </button>
          <RouterLink to="/sign-up">
            <button className="bg-gray-800 text-white border-2 border-gray-400 px-4 py-2 hover:font-bold rounded-md">Register Now</button>
          </RouterLink>
        </div>

        {/* Mobile Menu Toggle */}
        <div className="md:hidden">
          {showMenu ? (
            <FiX className="text-2xl cursor-pointer" onClick={() => setShowMenu(false)} />
          ) : (
            <FiMenu className="text-2xl cursor-pointer" onClick={() => setShowMenu(true)} />
          )}
        </div>
      </div>

      {/* Mobile Menu */}
      {showMenu && (
        <div className="absolute top-16 left-0 w-full bg-gray-200 shadow-md transition duration-300 ease-in-out">
          <ul className="flex flex-col px-6 py-4 space-y-4">
            {[
              { name: "Home", to: "/", section: "home" },
              { name: "About Us", to: "/#about", section: "about", isScroll: true },
              { name: "Product", to: "/gst-reconciliation", section: "product" },
              { name: "Contact Us", to: "/#contact", section: "contact", isScroll: true },
            ].map(({ name, to, section, isScroll }) => (
              <li key={name}>
                {isScroll ? (
                  <ScrollLink to={to} smooth onClick={() => handleScrollToSection(section)}>
                    <button className="hover:font-bold">{name}</button>
                  </ScrollLink>
                ) : (
                  <RouterLink to={to} onClick={() => handleScrollToSection(section)}>
                    <button className="hover:font-bold">{name}</button>
                  </RouterLink>
                )}
              </li>
            ))}

            {/* Mobile Pricing Dropdown */}
            <li className="flex flex-col">
              <button
                className="hover:font-bold flex items-center focus:outline-none text-left"
                onClick={() => setShowMobilePricingDropdown(!showMobilePricingDropdown)}
              >
                Pricing <FiChevronDown className="ml-1" />
              </button>
              
              {showMobilePricingDropdown && (
                <div className="ml-4 mt-2 space-y-3">
                  {/* GST Reconciliation Mobile Item */}
                  <RouterLink 
                    to="/pricing"
                    onClick={() => {
                      handleScrollToSection("pricing");
                      setShowMobilePricingDropdown(false);
                      closeMenu();
                    }}
                  >
                    <div className="block text-left hover:font-bold hover:text-gray-800 cursor-pointer py-1">
                      <div className="font-medium">GST Reconciliation</div>
                      <div className="text-sm text-gray-600">View pricing plans</div>
                    </div>
                  </RouterLink>
                  
                  {/* Ledger Pro Mobile Item */}
                  <RouterLink 
                    to="/ledger-pro"
                    onClick={() => {
                      setShowMobilePricingDropdown(false);
                      closeMenu();
                    }}
                  >
                    <div className="block text-left hover:font-bold hover:text-gray-800 cursor-pointer py-1">
                      <div className="font-medium">Ledger Pro</div>
                      <div className="text-sm text-gray-600">View pricing for Ledger Pro</div>
                    </div>
                  </RouterLink>
                </div>
              )}
            </li>

            <li>
              <button onClick={handleSignInClick} className="bg-gray-800 text-white border-2 border-gray-400 px-4 py-2 hover:font-bold rounded">
                Sign In
              </button>
            </li>
            <li>
              <RouterLink to="/sign-up">
                <button onClick={handleSignInClick} className="bg-gray-800 text-white border-2 border-gray-400 px-4 py-2 hover:font-bold rounded">Register Now</button>
              </RouterLink>
            </li>
          </ul>
        </div>
      )}
    </nav>
  );
}